import React, { useEffect, useState, useRef } from 'react';
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { GripVertical } from 'lucide-react';

const WhatsAppMsg = () => {
  const isLocal = window.location.hostname === 'localhost';
  const API_BASE_URL = isLocal ? 'https://slateblue-mallard-430836.hostingersite.com' : 'https://slateblue-mallard-430836.hostingersite.com';
  const [templates, setTemplates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [showAlert, setShowAlert] = useState(null);
  const [scrollTrigger, setScrollTrigger] = useState(0);
  const [confirmAction, setConfirmAction] = useState(null);
  const [showSuccessAlert, setShowSuccessAlert] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [userRole, setUserRole] = useState('');
  const topRef = useRef(null);

  const [formData, setFormData] = useState({
    title: "",
    message: "",
    location: ""
  });

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserRole(decoded.role || 'user');
      } catch (err) {
        console.error("Error decoding token:", err);
      }
    }
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompanyId) {
      fetchTemplates(selectedCompanyId);
    }
  }, [selectedCompanyId, userRole]);

  const fetchCompanies = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setError("No access token found. Please log in.");
      setLoading(false);
      return;
    }

    try {
      const decoded = jwtDecode(token);

      if (decoded.role === 'superadmin') {
        const response = await axios.get(
          `${API_BASE_URL}/api/companies`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCompanies(response.data || []);
        if (response.data.length > 0) {
          setSelectedCompanyId(response.data[0]._id);
        }
      } else if (decoded.role === 'company') {
        const companyId = decoded.companyId || decoded.id;
        const response = await axios.get(
          `${API_BASE_URL}/api/companies/${companyId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCompanies([response.data]);
        setSelectedCompanyId(companyId);
      } else {
        const companyId = decoded.company || decoded.companyId;
        const employeeId = decoded.id || decoded._id;
        try {
          const profileResponse = await axios.get(
            `${API_BASE_URL}/api/employees/${employeeId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const companyData = profileResponse.data?.company;
          if (companyData && typeof companyData === 'object') {
            setCompanies([{ _id: companyId, businessName: companyData.businessName }]);
          } else {
            setCompanies([{ _id: companyId, businessName: 'My Company' }]);
          }
        } catch (err) {
          setCompanies([{ _id: companyId, businessName: 'My Company' }]);
        }
        setSelectedCompanyId(companyId);
      }
    } catch (err) {
      console.error("Invalid token:", err);
      setError("Invalid token. Please log in again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async (companyId) => {
    const token = localStorage.getItem("authToken");
    if (!token) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/whatsapp-msg/company/${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTemplates(response.data || []);
    } catch (err) {
      console.error("Error fetching templates:", err);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(templates);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setTemplates(items);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddTemplate = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("authToken");

    if (!formData.title.trim() || !formData.message.trim()) {
      setShowAlert("Please fill in all fields.");
      return;
    }

    if (!selectedCompanyId) {
      setShowAlert("Please select a company first.");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/whatsapp-msg/company/${selectedCompanyId}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 201) {
        setShowSuccessAlert("Template created successfully!");
        fetchTemplates(selectedCompanyId);
        resetForm();
      }
    } catch (err) {
      setShowAlert(err.response?.data?.message || "Error creating template");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditTemplate = (template) => {
    setIsEditing(true);
    setEditingTemplateId(template._id);
    setFormData({
      title: template.title,
      message: template.message,
      location: template.location || ""
    });
    setShowForm(true);
    setScrollTrigger(prev => prev + 1);
  };

  const handleUpdateTemplate = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("authToken");

    setSubmitting(true);
    try {
      await axios.put(
        `${API_BASE_URL}/api/whatsapp-msg/${editingTemplateId}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowSuccessAlert("Template updated successfully!");
      fetchTemplates(selectedCompanyId);
      resetForm();
    } catch (err) {
      setShowAlert(err.response?.data?.message || "Error updating template");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTemplate = (id, title) => {
    setConfirmAction({
      type: 'delete',
      message: `Are you sure you want to delete the template "${title}"?`,
      id: id
    });
  };

  const handleConfirmAction = async () => {
    const token = localStorage.getItem("authToken");
    try {
      setSubmitting(true);
      if (confirmAction.type === 'delete') {
        await axios.delete(
          `${API_BASE_URL}/api/whatsapp-msg/${confirmAction.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setShowSuccessAlert('Template deleted successfully!');
        fetchTemplates(selectedCompanyId);
      }
    } catch (error) {
      setShowAlert(error.response?.data?.message || "Error deleting template");
    } finally {
      setSubmitting(false);
      setConfirmAction(null);
    }
  };

  const resetForm = () => {
    setFormData({ title: "", message: "", location: "" });
    setIsEditing(false);
    setEditingTemplateId(null);
    setShowForm(false);
  };

  if (loading && !templates.length) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 md:bg-gray-50 py-6 md:py-8 px-4">
      <div ref={topRef} className="max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 md:mb-8">
          <div className="mb-2 md:mb-0">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800 md:text-gray-800 flex items-center">
              <span className="md:hidden p-2 mr-3 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl text-white shadow-md">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                </svg>
              </span>
              WhatsApp Messages
            </h1>
            <p className="text-sm md:text-base text-slate-500 md:text-gray-600 mt-1 md:mt-2 ml-14 md:ml-0">Manage your WhatsApp message templates</p>
          </div>

          <div className="flex flex-col md:flex-row items-stretch md:items-center space-y-3 md:space-y-0 md:space-x-4 mt-4 md:mt-0">
            {userRole === 'superadmin' && (
              <select
                value={selectedCompanyId}
                onChange={(e) => setSelectedCompanyId(e.target.value)}
                className="px-4 py-2.5 md:py-2 bg-white border border-slate-200 md:border-gray-300 rounded-lg md:rounded-md text-sm md:text-base"
              >
                {companies.map((company) => (
                  <option key={company._id} value={company._id}>{company.businessName}</option>
                ))}
              </select>
            )}
            <button
              onClick={() => (showForm ? resetForm() : setShowForm(true))}
              className="w-full md:w-auto px-6 py-3 md:py-3 bg-gradient-to-r from-blue-500 to-cyan-500 md:bg-none md:bg-indigo-600 text-white font-semibold rounded-xl md:rounded-md hover:from-blue-600 hover:to-cyan-600 md:hover:bg-indigo-700 transition-all shadow-md flex justify-center items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 md:hidden" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
              </svg>
              {showForm ? "Cancel" : "Add Template"}
            </button>
          </div>
        </div>

        {showForm && (
          <div className="bg-white rounded-xl shadow-md md:shadow-lg p-5 md:p-6 mb-6 md:mb-8 border border-slate-100 md:border-gray-100">
            <h2 className="text-lg md:text-xl font-semibold mb-4 md:mb-6 text-slate-800">{isEditing ? "Edit Template" : "Add New Template"}</h2>
            <form onSubmit={isEditing ? handleUpdateTemplate : handleAddTemplate}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 md:text-gray-700 mb-1 md:mb-2">Template Title</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 md:py-2 text-sm md:text-base border border-slate-200 md:border-gray-300 rounded-lg md:rounded-md focus:ring-2 focus:ring-blue-100 md:focus:ring-0 focus:border-blue-500 md:focus:ring-indigo-500 md:focus:border-indigo-500 outline-none transition-colors"
                    required
                    placeholder="e.g., Welcome Message"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 md:text-gray-700 mb-1 md:mb-2">Message Content</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full px-4 py-2.5 md:py-2 text-sm md:text-base border border-slate-200 md:border-gray-300 rounded-lg md:rounded-md focus:ring-2 focus:ring-blue-100 md:focus:ring-0 focus:border-blue-500 md:focus:ring-indigo-500 md:focus:border-indigo-500 outline-none transition-colors resize-none"
                    required
                    placeholder="Enter your message template here..."
                  ></textarea>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 md:text-gray-700 mb-1 md:mb-2">Location (Optional)</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 md:py-2 text-sm md:text-base border border-slate-200 md:border-gray-300 rounded-lg md:rounded-md focus:ring-2 focus:ring-blue-100 md:focus:ring-0 focus:border-blue-500 md:focus:ring-indigo-500 md:focus:border-indigo-500 outline-none transition-colors"
                    placeholder="e.g., Office, Branch A, or Coordinates"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-2">
                  <button type="button" onClick={resetForm} className="flex-1 md:flex-none px-4 py-2.5 md:py-2 bg-slate-100 md:bg-transparent text-slate-700 border border-transparent md:border-gray-300 rounded-lg md:rounded-md font-medium">Cancel</button>
                  <button type="submit" disabled={submitting} className="flex-1 md:flex-none px-4 py-2.5 md:py-2 bg-gradient-to-r from-blue-500 to-cyan-500 md:bg-none md:bg-indigo-600 text-white rounded-lg md:rounded-md hover:from-blue-600 hover:to-cyan-600 md:hover:bg-indigo-700 font-medium shadow-sm">
                    {submitting ? "Saving..." : (isEditing ? "Update Template" : "Save Template")}
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        <div className="bg-transparent md:bg-white rounded-none md:rounded-xl shadow-none md:shadow-md overflow-hidden border-none md:border md:border-gray-100">
          <div className="hidden md:flex px-6 py-4 border-b border-gray-200 bg-gray-50 justify-between items-center">
            <h2 className="text-lg font-medium text-gray-800">Templates</h2>
            <span className="text-sm text-gray-600">{templates.length} templates found</span>
          </div>
          <div className="md:hidden flex items-center justify-between mb-4 mt-6">
            <h2 className="text-lg font-semibold text-slate-800">Your Templates</h2>
            <span className="text-xs font-medium px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full">{templates.length} templates</span>
          </div>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="templates">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="space-y-4 md:space-y-0 md:divide-y md:divide-gray-200"
                >
                  {templates.length === 0 ? (
                    <div className="p-8 text-center text-gray-500 bg-white rounded-xl md:rounded-none shadow-sm md:shadow-none">No templates found.</div>
                  ) : (
                    templates.map((template, index) => (
                      <Draggable key={template._id} draggableId={template._id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`p-0 md:p-6 transition-colors bg-transparent md:bg-white md:hover:bg-gray-50 ${snapshot.isDragging ? 'md:bg-indigo-50 shadow-xl md:shadow-lg rounded-xl md:rounded-none z-50' : ''}`}
                          >
                            {/* Desktop View */}
                            <div className="hidden md:flex justify-between items-start">
                              <div className="flex items-start flex-1">
                                <div
                                  {...provided.dragHandleProps}
                                  className="mt-1 mr-4 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing p-1 rounded hover:bg-gray-100"
                                >
                                  <GripVertical size={20} />
                                </div>
                                <div className="flex-1">
                                  <h3 className="text-lg font-medium text-gray-900">{template.title}</h3>
                                  <p className="text-gray-600 mt-2 whitespace-pre-wrap">{template.message}</p>
                                  {template.location && (
                                    <div className="mt-2 flex items-center text-sm text-gray-500">
                                      <span className="font-medium mr-1">📍 Location:</span> {template.location}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex space-x-2 ml-4">
                                <button onClick={() => handleEditTemplate(template)} className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-md">
                                  Edit
                                </button>
                                <button onClick={() => handleDeleteTemplate(template._id, template.title)} className="p-2 text-red-600 hover:bg-red-50 rounded-md">
                                  Delete
                                </button>
                              </div>
                            </div>

                            {/* Mobile View (Card) */}
                            <div className="md:hidden relative bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
                              <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-blue-500 to-cyan-500"></div>
                              <div className="p-4 pl-5">
                                <div className="flex justify-between items-start mb-3">
                                  <div className="flex items-start flex-1 mr-2">
                                    <div
                                      {...provided.dragHandleProps}
                                      className="mt-0.5 mr-2 text-slate-300 active:text-blue-500 p-1 -ml-1 cursor-grab"
                                    >
                                      <GripVertical size={18} />
                                    </div>
                                    <h3 className="text-base font-semibold text-slate-800 break-words line-clamp-2">{template.title}</h3>
                                  </div>
                                </div>

                                <div className="bg-slate-50 rounded-lg p-3 mb-3 border border-slate-100">
                                  <p className="text-sm text-slate-600 whitespace-pre-wrap break-words">{template.message}</p>
                                  {template.location && (
                                    <div className="mt-3 flex items-center text-xs text-slate-500 font-medium">
                                      <svg className="w-3.5 h-3.5 mr-1 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                      {template.location}
                                    </div>
                                  )}
                                </div>

                                <div className="flex justify-end space-x-2 pt-1">
                                  <button
                                    onClick={() => handleEditTemplate(template)}
                                    className="flex-1 flex justify-center items-center px-3 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors"
                                  >
                                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteTemplate(template._id, template.title)}
                                    className="flex-1 flex justify-center items-center px-3 py-2 bg-rose-50 text-rose-600 rounded-lg text-sm font-medium hover:bg-rose-100 transition-colors"
                                  >
                                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                    Delete
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    )
                    ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      </div>

      {confirmAction && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded-2xl shadow-lg p-6 w-[90%] max-w-sm text-center">
            <h2 className="text-lg font-semibold mb-4">Confirmation</h2>
            <p className="text-gray-700 mb-6">{confirmAction.message}</p>
            <div className="flex justify-center space-x-4">
              <button onClick={handleConfirmAction} className="px-4 py-2 bg-indigo-600 text-white rounded-md">Confirm</button>
              <button onClick={() => setConfirmAction(null)} className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showSuccessAlert && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded-2xl shadow-lg p-6 w-[90%] max-w-sm text-center">
            <h2 className="text-lg font-semibold mb-4 text-green-600">Success</h2>
            <p className="text-gray-700 mb-6">{showSuccessAlert}</p>
            <button onClick={() => setShowSuccessAlert(null)} className="px-4 py-2 bg-indigo-600 text-white rounded-md w-full">OK</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppMsg;
